import {ConfirmationDialog} from '@/components/ui/confirmation-dialog-atoms/ConfirmationDialogAtoms'
import {ConfirmationModalTitle} from '@/components/ui/confirmation-modal-atoms/ConfirmationModalAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {Text} from '@/components/ui/text/Text'
import {EditPlannedAccessConfirmationSchema} from '@/features/planned-accesses/formSchemas'
import {useEditPlannedAccesses} from '@/features/planned-accesses/queries/useEditPlannedAccesses'
import {plannedAccessesValuesToPayload} from '@/features/planned-accesses/utils'
import {useGetProject} from '@/features/projects/queries/useGetProject'
import {useTranslation} from '@/translations/i18n'
import {toast} from '@/utilities/toast/toast'
import {DialogStore} from '@ariakit/react'
import {FormProvider, useForm, useWatch} from 'react-hook-form'
import {useNavigate} from 'react-router-dom'
import {zodResolver} from '@hookform/resolvers/zod'

export const EditPlannedAccessesConfirmationDialog = ({
    onError,
    dialogStore,
    defaultValues
}: {
    onError: () => void
    dialogStore: DialogStore
    defaultValues: EditPlannedAccessConfirmationSchema
}) => {
    const {t} = useTranslation()
    const form = useForm<EditPlannedAccessConfirmationSchema>({
        resolver: zodResolver(EditPlannedAccessConfirmationSchema),
        defaultValues
    })
    const id = useWatch({control: form.control, name: 'id'})
    const projectId = useWatch({control: form.control, name: 'projectId'})
    const navigate = useNavigate()

    const {
        data: project,
        isLoading,
        isError
    } = useGetProject({
        urlParams: {projectId},
        params: {
            responseType: 'small'
        }
    })

    const {mutate: editPlannedAccesses, isPending} = useEditPlannedAccesses(projectId, id, {
        onSuccess: () => {
            toast.success(t('planned_accesses:planned_accesses_update_success'))
            navigate(-1)
        },
        onError: error => {
            console.error('Edit planned accesses error: ', error)
            onError()
        }
    })

    const handleConfirmSubmit = form.handleSubmit(
        formValues => {
            editPlannedAccesses({
                additionalNotes: formValues.additionalNotes,
                targets: formValues.objectives,
                treatmentPlan: formValues.treatmentsPlan,
                plannedAccesses: plannedAccessesValuesToPayload(formValues, project?.sector),
                notificationRecipientIds: formValues.notificationsRecipients?.map(recipient => recipient.id),
                notificationDetails: formValues.notificationDetails
            })
        },
        error => {
            console.error('Validation edit planned accesses error: ', error)
            onError()
        }
    )

    return (
        <FormProvider {...form}>
            <ConfirmationDialog
                dialogStore={dialogStore}
                onConfirm={handleConfirmSubmit}
                isConfirmPending={isPending}
                headerSlot={
                    <Flexbox direction="column">
                        <ConfirmationModalTitle>{t('planned_accesses:edit_planned_accesses')}</ConfirmationModalTitle>
                        {isLoading ? (
                            <Flexbox direction="column" gap={1.5}>
                                <Skeleton width={100} height={20} />
                                <Skeleton width={100} height={20} />
                            </Flexbox>
                        ) : isError ? (
                            <Text size="textSm" palette="danger" shade="500">
                                {t('errors:default')}
                            </Text>
                        ) : project?.patient.fullName ? (
                            <Text size="textSm" palette="neutral" shade="500">
                                {t('commons:patient_fullname_value', {fullName: project?.patient.fullName})}
                            </Text>
                        ) : (
                            <Text size="textSm" palette="neutral" shade="500">
                                {t('commons:no_results')}
                            </Text>
                        )}
                    </Flexbox>
                }
            />
        </FormProvider>
    )
}
